import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, FileText, Table, Star, CheckCircle2, Circle } from "lucide-react";

const EXCLUDED = ["moye", "dougherty"]; // never list excluded personnel

export default function PeopleTab() {
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [view, setView] = useState("table"); // table | resume
  const [filter, setFilter] = useState("all"); // all | pool | resume

  const load = () => {
    api.get("/contacts").then((r) => setPeople(r.data)).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const coName = (id) => companies.find((c) => c.id === id)?.name || "";

  const visible = people
    .filter((p) => !EXCLUDED.some((x) => (p.name || "").toLowerCase().includes(x)))
    .filter((p) => filter === "all" ? true : filter === "pool" ? p.staffing_pool : p.resume_on_file);

  const toggle = async (p, key) => {
    try {
      await api.put(`/records/contact/${p.id}`, { fields: { [key]: !p[key] } });
      setPeople((prev) => prev.map((x) => x.id === p.id ? { ...x, [key]: !x[key] } : x));
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="people-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-1.5">
          {[["all", "All"], ["pool", "In staffing pool"], ["resume", "Has resume"]].map(([k, l]) => (
            <button key={k} data-testid={`people-filter-${k}`} onClick={() => setFilter(k)}
              className={`text-xs px-3 py-1.5 rounded-full border ${filter === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-1 bg-zinc-100 rounded-md p-0.5">
          <ViewBtn active={view === "table"} onClick={() => setView("table")} testid="people-view-table" icon={Table} label="Table" />
          <ViewBtn active={view === "resume"} onClick={() => setView("resume")} testid="people-view-resume" icon={FileText} label="Resume" />
        </div>
      </div>

      {visible.length === 0 && <Empty />}

      {view === "table" && visible.length > 0 && (
        <div className="bg-white border border-zinc-200 shadow-sm rounded-xl overflow-hidden" data-testid="people-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500">
              <tr><Th>Name</Th><Th>Title / Role</Th><Th>Company</Th><Th>Contact</Th><Th>Pool</Th><Th>Resume</Th></tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr key={p.id} data-testid={`person-row-${p.id}`} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <Td><button data-testid={`person-name-${p.id}`} onClick={() => navigate(`/r/contact/${p.id}`)} className="text-zinc-900 hover:text-gold-dark font-medium">{p.name}</button></Td>
                  <Td><span className="text-zinc-700">{p.title || "—"}</span><div className="text-[11px] text-zinc-400">{p.role}</div></Td>
                  <Td><span className="text-zinc-600">{coName(p.company_id) || "—"}</span></Td>
                  <Td><div className="text-[11px] text-zinc-600">{p.phone}</div><div className="text-[11px] text-zinc-400">{p.email}</div></Td>
                  <Td><Toggle on={p.staffing_pool} onClick={() => toggle(p, "staffing_pool")} testid={`pool-toggle-${p.id}`} /></Td>
                  <Td><Toggle on={p.resume_on_file} onClick={() => toggle(p, "resume_on_file")} testid={`resume-toggle-${p.id}`} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === "resume" && visible.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="people-resume-cards">
          {visible.map((p) => (
            <button key={p.id} onClick={() => navigate(`/r/contact/${p.id}`)} data-testid={`person-card-${p.id}`}
              className="text-left bg-white border border-zinc-200 shadow-sm rounded-xl p-4 hover:border-gold-dark transition-colors">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-zinc-900">{p.name}</span>
                {p.staffing_pool && <Star className="h-4 w-4 text-gold-dark" />}
              </div>
              <div className="text-[12px] text-zinc-500">{p.title || p.role}</div>
              <div className="text-[11px] text-zinc-400 mt-1">{coName(p.company_id)}</div>
              <div className={`mt-3 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${p.resume_on_file ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                {p.resume_on_file ? <><CheckCircle2 className="h-3 w-3" /> Resume on file</> : "No resume"}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const ViewBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded ${active ? "bg-white shadow-sm text-zinc-900 font-semibold" : "text-zinc-500"}`}>
    <Icon className="h-3.5 w-3.5" /> {label}</button>
);
const Th = ({ children }) => <th className="text-left font-semibold px-3 py-2">{children}</th>;
const Td = ({ children }) => <td className="px-3 py-2 align-top">{children}</td>;
const Toggle = ({ on, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className={on ? "text-green-600" : "text-zinc-300 hover:text-zinc-500"}>
    {on ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}</button>
);
const Empty = () => (
  <div className="text-xs text-zinc-400 py-10 text-center border border-dashed border-zinc-300 rounded-xl flex flex-col items-center gap-2">
    <Users className="h-6 w-6 text-zinc-300" /> No people match this filter.</div>
);
